<template>
  <div class="col px-0 pr-xl-5 mb-5 fds" v-if="shouldShowComponent(objectModelPropertyType)">
    <label
      :for="`entry_field_${propertyClientId}`"
      :class="{required: objectModelProperty.is_required}"
      v-tooltip.wrap="canEdit ? null : tooltip"
    >
      {{ propertyLabelTitle }}<required-indicator v-if="objectModelProperty.is_required" />
    </label>
    <validation-error-wrapper :object-model="objectModel" :property-client-id="propertyClientId">
      <component
        :is="getComponentForPropertyType(objectModelPropertyType)"
        :id="`entry_field_${propertyClientId}`"
        class="mb-0"
        :class="{ 'read-only' : !canEdit }"
        :property-client-id="propertyClientId"
        :object-model="objectModel"
        v-bind="getAdditionalAttrs(objectModelProperty)"
        :entry="objectModelEntry"
        v-model="objectModelEntry[`f_${propertyClientId}`]"
        @input="revalidate"
        :isDetailViewEdit="isDetailViewEdit"
        :format-as-percentage="formatAsPercentage"
        @queue-upload-for-deletion="queueUploadForDeletion"
        :disabled="!canEdit"
        :disabled-tooltip="tooltip"
        :is-read-only="!canEdit"
      ></component>
      <span v-if="showRangeMessage" :class="rangeMessageClass">
        {{rangeMessage}}
      </span>
    </validation-error-wrapper>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import objectModelEntryPropertiesMixin from "~/mixins/objectModelEntries/objectModelEntryPropertiesMixin";
  import {ObjectModelPropertyRootType} from "../../enums/objectModelPropertyType";
  import ReadOnlyTextInput from './object-model-entry-read-only-text-input.vue';
  import pluralizeable from "../../mixins/pluralizeable";
  import {OptionRangeType} from "../../enums/optionRangeType";
  import ValidationErrorWrapper from "../../../formability/manageObjectModelEntry/components/object-model-entry-validation-error-wrapper.vue";
  import objectModelEntryInputFieldMixin from "~/mixins/objectModelEntries/objectModelEntryInputFieldMixin";
  import ObjectModelEntryFieldDefinition from "../../../formability/manageObjectModelEntry/models/objectModelEntryFieldDefinition";
  import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
  export default Vue.extend({
    mixins: [objectModelEntryPropertiesMixin, objectModelEntryInputFieldMixin],
    props:{
      objectModelEntry:{
        required: true,
        type: Object as () => ObjectModelEntry,
      },
    },
    components: {
      'read-only-text-input' : ReadOnlyTextInput,
      ValidationErrorWrapper,
    },
    computed: {

      showRangeMessage(): boolean {
        switch (this.objectModelPropertyType) {
          case ObjectModelPropertyRootType.Text:
          case ObjectModelPropertyRootType.LongText:
            return this.objectModelProperty.character_count.show_char_limit;
          case ObjectModelPropertyRootType.PickList:
            return this.objectModelProperty.is_required;
          case ObjectModelPropertyRootType.Number:
            return true;
        }
      },
      rangeMessageClass(): string | Array<string> {
        switch (this.objectModelPropertyType) {
          case ObjectModelPropertyRootType.Text:
          case ObjectModelPropertyRootType.LongText:
            return 'char-counter';
          case ObjectModelPropertyRootType.PickList:
            return 'option-range';
          case ObjectModelPropertyRootType.Number:
            return 'numeric-range';
        }
      },
      rangeMessage(): string {
        switch (this.objectModelPropertyType) {
          case ObjectModelPropertyRootType.Text:
          case ObjectModelPropertyRootType.LongText:
            return this.textRangeMessage;
          case ObjectModelPropertyRootType.PickList:
            return this.pickListRangeMessage;
          case ObjectModelPropertyRootType.Number:
            return this.numericRangeMessage;
        }
      },
      textRangeMessage(): string {
        const {min, max} = this.objectModelProperty.character_count;
        if (min && max) {
          return `${min}-${max} characters required`;
        } else if (min) {
          return `At least ${pluralizeable.filters.pluralize('character', min)} required`;
        } else if (max) {
          return `At most ${pluralizeable.filters.pluralize('character', max)} required`;
        }
      },
      pickListRangeMessage(): string {
        const min = this.objectModelProperty.checkbox_range_min,
          max = this.objectModelProperty.checkbox_range_max;
        switch (this.objectModelProperty.option_range_type) {
          case OptionRangeType.AtLeast:
            if (min) {
              return `At least ${pluralizeable.filters.pluralize('option', min)}`;
            }
            break;
          case OptionRangeType.AtMost:
            if (max) {
              return `Up to ${pluralizeable.filters.pluralize('option', max)}`;
            }
            break;
          case OptionRangeType.Between:
            if (min && max) {
              return `Between ${min} and ${max} options`
            } else if (min) {
              return `At least ${pluralizeable.filters.pluralize('option', min)}`;
            } else if (max) {
              return `At most ${pluralizeable.filters.pluralize('option', max)}`;
            }
            break;
          case OptionRangeType.Exactly:
            if (min) {
              return `Exactly ${pluralizeable.filters.pluralize('option', min)}`;
            }
            break;
          case OptionRangeType.All:
            return 'All options';
        }
      },
      numericRangeMessage(): string {
        const min = this.objectModelProperty.min_value,
          max = this.objectModelProperty.max_value;
        if (this.objectModelProperty.format_message) {
          return this.objectModelProperty.format_message;
        } else if (min && max) {
          return `Between ${min} and ${max}`;
        } else if (min) {
          return `At least ${min}`;
        } else if (max) {
          return `At most ${max}`;
        }
      },
      canEdit(): boolean {
        return this.$can('edit', this.objectModelProperty, { objectModel: this.objectModelEntry.published_object_model })
      },
      tooltip(): string {
        return "You do not have permission to edit this field."
      },
      propertyLabelTitle() {
        return this.labelTitle || this.objectModelProperty.labelTitleDisplay;
      }
    },
    methods: {
      shouldShowComponent(property_type: ObjectModelPropertyRootType): boolean {
        return this.isDetailViewEdit ? true : ObjectModelEntryFieldDefinition.showFieldComponent(property_type);
      },
      revalidate() {
        // if there is no validator, the validations haven't run yet (i.e. the user hasn't clicked the save button) so don't try to revalidate
        if (this.objectModelEntry.validator) {
          this.objectModelEntry.validate();
        }
      },
      // daisy chaining the event from grandchild to grandparent
      queueUploadForDeletion(upload): void {
        this.$emit('queue-upload-for-deletion', upload);
      },
    },
  });
</script>